import React from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Loader2 } from "lucide-react";
import { useMemberAuth } from "../hooks/useMemberAuth";
import { useOperativeCard } from "../hooks/useOperativeCard";
import OperativeCard from "../components/operative-card/OperativeCard";
import CardActions from "../components/operative-card/CardActions";
import CardSettings from "../components/operative-card/CardSettings";

const OperativeCardPage = () => {
  const { isMember, loading: authLoading } = useMemberAuth();
  const { card, loading, error, patchSettings } = useOperativeCard();

  if (authLoading || loading) {
    return (
      <div style={{ minHeight: "60vh", display: "flex", alignItems: "center", justifyContent: "center", backgroundColor: "#08070A" }}>
        <div className="text-center">
          <Loader2 size={20} className="animate-spin mx-auto text-[#A39E93]" />
          <div className="font-mono text-[9px] uppercase tracking-[0.3em] mt-3 text-[#6F6A60]">
            COMPILING CREDENTIAL
          </div>
        </div>
      </div>
    );
  }

  if (!isMember) {
    return (
      <div style={{ minHeight: "60vh", backgroundColor: "#08070A" }} className="flex items-center justify-center px-6">
        <div className="border p-6 max-w-md text-center" style={{ borderColor: "#2D2A26" }}>
          <div className="font-mono text-[10px] uppercase tracking-[0.4em] text-[#D92D20]">
            // RESTRICTED · MEMBER ONLY
          </div>
          <div className="font-display text-2xl mt-3 text-[#E4DFD3]">Sign in required</div>
          <p className="text-sm text-[#A39E93] mt-3">
            The Operative Card is generated only for authenticated members of The Archive.
          </p>
          <Link to="/members" className="inline-block mt-4 font-mono text-[10px] uppercase tracking-[0.3em] border px-3 py-1.5"
                style={{ borderColor: "#9FA8D4", color: "#9FA8D4" }}
                data-testid="card-page-signin">
            Go to membership
          </Link>
        </div>
      </div>
    );
  }

  if (error || !card) {
    return (
      <div style={{ minHeight: "60vh", backgroundColor: "#08070A" }} className="flex items-center justify-center">
        <div className="font-mono text-[10px] text-[#D92D20]">{error || "Card unavailable"}</div>
      </div>
    );
  }

  if (!card.callsign) {
    return (
      <div style={{ minHeight: "60vh", backgroundColor: "#08070A" }} className="flex items-center justify-center px-6">
        <div className="border p-6 max-w-md text-center" style={{ borderColor: "#2D2A26" }} data-testid="card-no-callsign">
          <div className="font-mono text-[10px] uppercase tracking-[0.4em] text-[#D92D20]">
            // PRE-CREDENTIAL · NO CALLSIGN
          </div>
          <div className="font-display text-2xl mt-3 text-[#E4DFD3]">Pick your callsign first</div>
          <p className="text-sm text-[#A39E93] mt-3">
            Your Operative Card is generated from your callsign. Visit your Member Desk and choose one, then come back.
          </p>
          <Link to="/members" className="inline-block mt-4 font-mono text-[10px] uppercase tracking-[0.3em] border px-3 py-1.5"
                style={{ borderColor: "#9FA8D4", color: "#9FA8D4" }}
                data-testid="card-no-callsign-action">
            Go to Member Desk
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div style={{ minHeight: "100vh", backgroundColor: "#08070A" }} data-testid="operative-card-page">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 md:px-10 py-8">
        <Link
          to="/members"
          className="inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.3em] text-[#A39E93] hover:text-[#D92D20] mb-6"
          data-testid="card-back-to-desk"
        >
          <ArrowLeft size={12} /> Back to Member Desk
        </Link>

        <header className="mb-6">
          <div className="font-mono text-[10px] uppercase tracking-[0.4em] text-[#D92D20]">
            // OPERATIVE CREDENTIAL · DOWNLOAD &amp; SHARE
          </div>
          <h1 className="font-display text-3xl sm:text-4xl mt-2 text-[#E4DFD3]">
            Your Operative Card
          </h1>
          <p className="text-sm text-[#A39E93] mt-2 max-w-2xl">
            A shareable intelligence credential issued by The Archive. Contains zero personal
            information — only your callsign, archive serial, clearance, and earned badges.
          </p>
        </header>

        <div className="grid grid-cols-1 lg:grid-cols-[auto_1fr] gap-8">
          {/* Card + downloads/share */}
          <div className="overflow-hidden">
            <div style={{ width: 650, maxWidth: "100%" }}>
              <CardActions card={card} />
            </div>
          </div>

          {/* Settings */}
          <div>
            <CardSettings card={card} onPatch={patchSettings} />
          </div>
        </div>

        <div className="font-mono text-[9px] uppercase tracking-[0.4em] text-[#6F6A60] text-center mt-10">
          // NEVER LEAKS EMAIL · REAL NAME · MEMBER ID · BOOKMARKS · ADMIN ROLES
        </div>
      </div>
    </div>
  );
};

export default OperativeCardPage;
